import React, { useState, useCallback } from 'react';
import { Radio, Copy, Check, RefreshCw, Play, Square, Trash2, Plus, X, ExternalLink, Tv } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/api';
import { toast } from 'sonner';

const StreamPanel = ({ game, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [streamData, setStreamData] = useState(null);
  const [showSimulcast, setShowSimulcast] = useState(false);
  const [simulcastUrl, setSimulcastUrl] = useState('');
  const [simulcastKey, setSimulcastKey] = useState('');
  const [simulcastLabel, setSimulcastLabel] = useState('');
  const [copiedField, setCopiedField] = useState(null);

  const hasMux = !!game.mux_live_stream_id;

  const copyText = useCallback(async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const i = document.createElement('input');
      i.value = text; document.body.appendChild(i); i.select(); document.execCommand('copy'); document.body.removeChild(i);
    }
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  }, []);

  const withLoading = useCallback(async (fn) => {
    setLoading(true);
    try { await fn(); } catch (e) {
      toast.error(e.response?.data?.detail || 'Operation failed');
    } finally { setLoading(false); }
  }, []);

  const createStream = () => withLoading(async () => {
    const res = await api.createMuxStream(game.id);
    setStreamData(res.data);
    onUpdate?.();
    toast.success('Mux stream created');
  });

  const refreshStatus = () => withLoading(async () => {
    const res = await api.getMuxStreamStatus(game.id);
    setStreamData(res.data);
    onUpdate?.();
  });

  const enableStream = () => withLoading(async () => {
    await api.enableMuxStream(game.id);
    await refreshStatus();
    toast.success('Stream enabled');
  });

  const disableStream = () => withLoading(async () => {
    await api.disableMuxStream(game.id);
    await refreshStatus();
    toast.success('Stream disabled (stopped)');
  });

  const completeStream = () => withLoading(async () => {
    await api.completeMuxStream(game.id);
    await refreshStatus();
    toast.success('Stream completed');
  });

  const resetKey = () => withLoading(async () => {
    if (!window.confirm('Reset stream key? The old key will stop working.')) return;
    const res = await api.resetMuxStreamKey(game.id);
    setStreamData(prev => prev ? { ...prev, stream_key: res.data.stream_key } : prev);
    onUpdate?.();
    toast.success('Stream key reset');
  });

  const deleteStream = () => withLoading(async () => {
    if (!window.confirm('Delete this Mux stream permanently?')) return;
    await api.deleteMuxStream(game.id);
    setStreamData(null);
    onUpdate?.();
    toast.success('Mux stream deleted');
  });

  const addSimulcast = () => withLoading(async () => {
    if (!simulcastUrl || !simulcastKey) return toast.error('URL and key are required');
    await api.addSimulcastTarget(game.id, { url: simulcastUrl, stream_key: simulcastKey, passthrough: simulcastLabel });
    setSimulcastUrl(''); setSimulcastKey(''); setSimulcastLabel('');
    setShowSimulcast(false);
    await refreshStatus();
    toast.success('Simulcast target added');
  });

  const removeSimulcast = (targetId) => withLoading(async () => {
    await api.removeSimulcastTarget(game.id, targetId);
    await refreshStatus();
    toast.success('Simulcast target removed');
  });

  const sk = streamData?.stream_key || game.mux_stream_key;
  const pid = streamData?.playback_id || game.mux_playback_id;
  const status = streamData?.status || game.mux_stream_status || 'unknown';
  const rtmpUrl = 'rtmp://global-live.mux.com:5222/app';

  const statusColor = {
    active: 'bg-red-500', idle: 'bg-yellow-500', disabled: 'bg-gray-500',
  }[status] || 'bg-gray-500';

  const watchUrl = `${window.location.origin}/watch/${game.id}`;

  // Not yet created
  if (!hasMux) {
    return (
      <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Tv className="w-5 h-5 text-gray-400" />
            <span className="text-gray-300 text-sm">No Mux stream configured</span>
          </div>
          <Button size="sm" onClick={createStream} disabled={loading} className="bg-sky-500 hover:bg-sky-600 text-white" data-testid="create-mux-stream-btn">
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Radio className="w-4 h-4 mr-1" />}
            Create Stream
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-800/50 rounded-lg border border-gray-700 overflow-hidden" data-testid="stream-panel">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-700 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Tv className="w-5 h-5 text-sky-400" />
          <span className="text-white font-medium text-sm">Mux Live Stream</span>
          <Badge className={`${statusColor} text-white text-xs`}>{status.toUpperCase()}</Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={refreshStatus} disabled={loading} className="text-gray-400 hover:text-white h-8 px-2" data-testid="refresh-stream-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button variant="ghost" size="sm" onClick={deleteStream} disabled={loading} className="text-red-400 hover:text-red-300 h-8 px-2" data-testid="delete-stream-btn">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* RTMP Credentials */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-gray-400 text-xs mb-1">RTMP URL</Label>
            <div className="flex items-center gap-1">
              <code className="bg-black/40 text-green-400 px-3 py-1.5 rounded text-xs flex-1 font-mono truncate">{rtmpUrl}</code>
              <Button variant="ghost" size="sm" onClick={() => copyText(rtmpUrl, 'url')} className="h-8 px-2 text-gray-400 hover:text-white">
                {copiedField === 'url' ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
              </Button>
            </div>
          </div>
          <div>
            <Label className="text-gray-400 text-xs mb-1">Stream Key</Label>
            <div className="flex items-center gap-1">
              <code className="bg-black/40 text-amber-400 px-3 py-1.5 rounded text-xs flex-1 font-mono truncate">{sk || '—'}</code>
              <Button variant="ghost" size="sm" onClick={() => sk && copyText(sk, 'key')} className="h-8 px-2 text-gray-400 hover:text-white">
                {copiedField === 'key' ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Shareable Watch Link */}
        <div>
          <Label className="text-gray-400 text-xs mb-1">Watch Link (shareable — works live & replay)</Label>
          <div className="flex items-center gap-1">
            <code className="bg-black/40 text-sky-400 px-3 py-1.5 rounded text-xs flex-1 font-mono truncate">{watchUrl}</code>
            <Button variant="ghost" size="sm" onClick={() => copyText(watchUrl, 'watch')} className="h-8 px-2 text-gray-400 hover:text-white">
              {copiedField === 'watch' ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
            </Button>
            <a href={watchUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center h-8 px-2 text-gray-400 hover:text-white">
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap gap-2 pt-1">
          {status === 'disabled' && (
            <Button size="sm" onClick={enableStream} disabled={loading} className="bg-green-600 hover:bg-green-700 text-white" data-testid="enable-stream-btn">
              <Play className="w-4 h-4 mr-1" /> Enable Stream
            </Button>
          )}
          {(status === 'idle' || status === 'active') && (
            <Button size="sm" onClick={disableStream} disabled={loading} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="disable-stream-btn">
              <Square className="w-4 h-4 mr-1" /> Stop Stream
            </Button>
          )}
          {status === 'active' && (
            <Button size="sm" onClick={completeStream} disabled={loading} variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-700" data-testid="complete-stream-btn">
              Complete & Save Recording
            </Button>
          )}
          <Button size="sm" onClick={resetKey} disabled={loading} variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-700">
            <RefreshCw className="w-4 h-4 mr-1" /> Reset Key
          </Button>
        </div>

        {/* Simulcast Targets */}
        <div className="border-t border-gray-700 pt-3">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-gray-400 text-xs">Simulcast Destinations</Label>
            <Button variant="ghost" size="sm" onClick={() => setShowSimulcast(!showSimulcast)} className="text-sky-400 hover:text-sky-300 h-7 text-xs">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add Destination
            </Button>
          </div>

          {streamData?.simulcast_targets?.length > 0 && (
            <div className="space-y-1.5 mb-2">
              {streamData.simulcast_targets.map(t => (
                <div key={t.id} className="flex items-center justify-between bg-black/30 rounded px-3 py-1.5">
                  <div className="flex items-center gap-2">
                    <Badge className={`text-xs ${t.status === 'broadcasting' ? 'bg-green-600' : 'bg-gray-600'} text-white`}>
                      {t.status}
                    </Badge>
                    <span className="text-gray-300 text-xs truncate max-w-[200px]">{t.passthrough || t.url}</span>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => removeSimulcast(t.id)} className="text-red-400 hover:text-red-300 h-6 px-1">
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {showSimulcast && (
            <div className="bg-black/30 rounded-lg p-3 space-y-2">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <div>
                  <Label className="text-gray-500 text-xs">RTMP URL</Label>
                  <Input value={simulcastUrl} onChange={e => setSimulcastUrl(e.target.value)} placeholder="rtmp://..." className="h-8 text-xs bg-gray-900 border-gray-700 text-white" />
                </div>
                <div>
                  <Label className="text-gray-500 text-xs">Stream Key</Label>
                  <Input value={simulcastKey} onChange={e => setSimulcastKey(e.target.value)} placeholder="stream key" className="h-8 text-xs bg-gray-900 border-gray-700 text-white" />
                </div>
                <div>
                  <Label className="text-gray-500 text-xs">Label (optional)</Label>
                  <Input value={simulcastLabel} onChange={e => setSimulcastLabel(e.target.value)} placeholder="YouTube, Facebook..." className="h-8 text-xs bg-gray-900 border-gray-700 text-white" />
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={addSimulcast} disabled={loading} className="bg-sky-500 hover:bg-sky-600 text-white text-xs h-7">
                  Add
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setShowSimulcast(false)} className="text-gray-400 text-xs h-7">
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StreamPanel;
